/**
 * Emit hosts for the `integrations` analytics group — 18 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type HaikuTabChangedEventProps,
  type IntegrationCreatedEventProps,
  type IntegrationDataFailedEventProps,
  type IntegrationDataRetrievedEventProps,
  type IntegrationFailedEventProps,
  type IntegrationNoteSyncedEventProps,
  type IntegrationPatientSelectedEventProps,
  type IntegrationSessionCreatedEventProps,
  type IntegrationStartedEventProps,
  type IntegrationType,
  type IntegrationWidgetAuthStepCompletedEventProps,
  type IntegrationWidgetAuthStepErroredEventProps,
  type IntegrationWidgetAuthStepMiscEventProps,
  type TokenGeneratedEventProps,
} from '@/models/analytics/features/integrations';
import { SegmentActionName, SegmentObjectName } from '@/models/analytics/segment-names';
import { type CreateEHRProfileRequest } from '@/types';
import { type EHRProvider } from '@heidi/schema/ml-scribe';

export function trackConnectIntegration(userInfo: CreateEHRProfileRequest) {
  trackLegacyEvent({
    name: SegmentObjectName.Integrations,
    props: {
      action: SegmentActionName.IntegrationsConnectIntegration,
      userInfo: userInfo,
    },
  });

  // V2
  track({
    event_name: TrackingEventName.IntegrationLinked,
    props: {},
  });
}

/**
 * Track Haiku tab navigation
 */
export function trackHaikuTabChanged(input: HaikuTabChangedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.HaikuTabChanged,
    props: {
      ...input,
      integration_type: 'haiku',
    },
  });
}

// ——— Integration Events ———————————————————————————————————————————————————————

/**
 * Track integration created event
 * Fired when an integration connection is established/subscribed
 */
export function trackIntegrationCreated(input: IntegrationCreatedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationCreated,
    props: input,
  });
}

export function trackIntegrationDataFailed(input: IntegrationDataFailedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationDataFailed,
    props: input,
  });
}

/**
 * Track integration data retrieved event
 * Fired when integration data is retrieved (e.g., patient details/demographics)
 */
export function trackIntegrationDataRetrieved(input: IntegrationDataRetrievedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationDataRetrieved,
    props: input,
  });
}

export function trackIntegrationFailed(input: IntegrationFailedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationFailed,
    props: input,
  });
}

/**
 * Track integration patient selected event
 * Fired when a patient is selected/switched via an integration
 */
export function trackIntegrationPatientSelected(
  input: IntegrationPatientSelectedEventProps
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationPatientSelected,
    props: input,
  });
}

/**
 * Track integration session created event
 * Fired when a new session is created via an integration
 */
export function trackIntegrationSessionCreated(
  input: IntegrationSessionCreatedEventProps
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationSessionCreated,
    props: input,
  });
}

// integrations
export function trackIntegrationStarted(input: IntegrationStartedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationStarted,
    props: input,
  });
}

export function trackIntegrationWidgetAuthStepCompleted(
  input: IntegrationWidgetAuthStepCompletedEventProps
) {
  const completionActionByStep = {
    'sign-in': 'widget_auth_sign_in_completed',
    'select-workspace': 'widget_auth_select_workspace_completed',
    'link-account': 'widget_auth_link_account_completed',
    'upgrade-plan': 'widget_link_upgrade_complete',
  } as const;

  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationCompleted,
    props: {
      integration_type: input.integration_type,
      completion_action: completionActionByStep[input.step],
    },
  });
}

export function trackIntegrationWidgetAuthStepErrored(
  input: IntegrationWidgetAuthStepErroredEventProps
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationWidgetAuthStepErrored,
    props: input,
  });

  // V2 — consolidated integration_failed
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationFailed,
    props: {
      integration_type: input.integration_type,
      failure_action: 'widget_auth_step_errored',
    },
  });
}

export function trackIntegrationWidgetAuthStepMisc(
  input: IntegrationWidgetAuthStepMiscEventProps
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationWidgetAuthStepMisc,
    props: input,
  });
}

/**
 * Track note sync failed event
 * Fired when a note failed to sync/push to an EHR
 */
export function trackNoteSyncFailed(input: IntegrationNoteSyncedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.NoteSyncFailed,
    props: input,
  });
}

/**
 * Track note synced event
 * Fired when a note is synced/pushed to an EHR
 */
export function trackNoteSynced(input: IntegrationNoteSyncedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.NoteSynced,
    props: input,
  });
}

export function trackTokenGenerated(input: TokenGeneratedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.TokenGenerated,
    props: input,
  });
}

export function widgetLinkAccountUnlinkSwitch(params: { integrationName?: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Integration,
    props: {
      ehrProvider: params.integrationName as EHRProvider, // this is just a string anyway
      action: SegmentActionName.WidgetLinkAccountUnlinkSwitch,
    },
  });

  // V2
  track({
    event_name: TrackingEventName.IntegrationUnlinked,
    props: {
      integration_type: (params.integrationName as IntegrationType) ?? 'other',
    },
  });
}

export function widgetLinkEhrAccountAlreadyLinkedToHeidi(params: {
  integrationName?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Integration,
    props: {
      ehrProvider: params.integrationName as EHRProvider, // this is just a string anyway
      action: SegmentActionName.WidgetLinkEhrAccountAlreadyLinkedToHeidi,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationFailed,
    props: {
      integration_type: (params.integrationName as IntegrationType) ?? 'other',
      failure_action: 'ehr_account_already_linked_to_heidi',
    },
  });
}

export function widgetLinkHeidiAccountAlreadyLinkedToEhr(params: {
  integrationName?: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Integration,
    props: {
      ehrProvider: params.integrationName as EHRProvider, // this is just a string anyway
      action: SegmentActionName.WidgetLinkHeidiAccountAlreadyLinkedToEhr,
    },
  });

  // V2
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.IntegrationFailed,
    props: {
      integration_type: (params.integrationName as IntegrationType) ?? 'other',
      failure_action: 'heidi_account_already_linked_to_ehr',
    },
  });
}
